import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { useLocation } from 'react-router-dom';
import Spinner from 'sushiweb/Spinner';
import { red } from 'sushiweb/tokens/color';
import ToastContainer from 'sushiweb/ToastContainer';
import Toast from 'sushiweb/Toast';
import { GET_AUTH_VALIDATE } from '../../constants/urls';
import zfetch, { METHOD_POST } from '../common/utils/zfetch';
import { clearCookie, decryptData } from '../../helpers';
import { createGiftCard, getGiftCardDetails } from '../../api/giftCard';
import { genericErrorMsg } from '../../constants';
import {
  PageWrapper,
  LoadingContainer,
  Container,
  LoadingContent,
  SpinnerContainer,
  LoadingTitle,
  LoadingSubtitle,
} from './styles';
import { GcStatusEnum } from '../common/components/constants';
import GiftCardDetails from './GiftCardDetails';
import GiftCardGenerating from './GiftCardGenerating';
import GiftCardEntitySelection from './GiftCardEntitySelection';
import GiftCardClaimed from './GiftCardClaimed';
import GiftCardTimeout from './GiftCardTimeout';
import GiftCardExpired from './GiftCardExpired';
import { openErrorToast } from '../../reducers/uiLogic';

const hashKey = process.env.REACT_APP_GIFT_CARD_HASH_KEY || '1111111111111111';

const GIFT_CARD_EXPIRY_DATE = new Date('2026-01-31T23:59:59');

const isGiftCardExpired = () => new Date() > GIFT_CARD_EXPIRY_DATE;

const renderContent = (
  gcStatus,
  gcData,
  reason,
  onGenerateCode,
  referenceId,
  onStatusChange,
) => {
  switch (gcStatus) {
    case GcStatusEnum.PROCESSING:
      return (
        <GiftCardGenerating
          referenceId={referenceId}
          onStatusChange={onStatusChange}
        />
      );
    case GcStatusEnum.GENERATED:
      return <GiftCardDetails data={gcData} />;
    case GcStatusEnum.CLAIMED:
      return <GiftCardClaimed />;
    case GcStatusEnum.TIMEOUT:
      return <GiftCardTimeout />;
    default:
      return (
        <GiftCardEntitySelection
          onGenerateCode={onGenerateCode}
          gcData={gcData}
        />
      );
  }
};

const GiftCardPage = ({ showErrorToast, toastDetails, removeToast }) => {
  const [loading, setLoading] = useState(true);
  const [gcStatus, setGcStatus] = useState('');
  const [gcData, setGcData] = useState({});
  const [reason, setReason] = useState('');
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const referenceId = searchParams.get('reference_id');

  const validateAuth = async () => {
    try {
      await clearCookie('__Host-csrft');

      const response = await zfetch(GET_AUTH_VALIDATE, {
        method: METHOD_POST,
      });

      const data = await response.json();
      console.log('Auth validation response:', data);
    } catch (err) {
      console.error('Auth validation failed:', err);
    }
  };

  const fetchGiftCardData = async () => {
    if (!referenceId) return;

    setLoading(true);

    try {
      const res = await getGiftCardDetails(referenceId);
      const { statusResponse } = res || {};

      if (!statusResponse?.status) {
        const errorMessage = statusResponse?.message || genericErrorMsg;
        console.error('Failed to fetch gift card details:', errorMessage);
        showErrorToast(errorMessage);
        return;
      }

      const {
        gift_card_status: giftCardStatus,
        status_reason,
        encrypted_payload: encryptedPayload,
      } = res;

      setGcStatus(giftCardStatus);
      setReason(status_reason);

      if (giftCardStatus === GcStatusEnum.FAILED) {
        openErrorToast('Gift card processing failed');
      }

      if (encryptedPayload) {
        const decryptedData = await decryptData(encryptedPayload, hashKey);
        setGcData(decryptedData);
      }
    } catch (error) {
      console.error('Gift card fetch error:', error);
      showErrorToast('Failed to fetch gift card details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isGiftCardExpired()) return;

    const initializeFlow = async () => {
      await validateAuth();
      await fetchGiftCardData();
    };

    initializeFlow();
  }, [referenceId]);

  const handleGenerateCode = async selectedEntity => {
    setGcStatus(GcStatusEnum.PROCESSING);
    try {
      const res = await createGiftCard({
        entity_type: selectedEntity,
        reference_id: referenceId,
      });
      const { statusResponse } = res || {};

      if (!statusResponse?.status) {
        const errorMessage = statusResponse?.message || genericErrorMsg;
        showErrorToast(errorMessage);
        setGcStatus(GcStatusEnum.INITIATED);
        return;
      }
    } catch (error) {
      console.error('Gift card creation error:', error);
      showErrorToast('Failed to create gift card');
      setGcStatus(GcStatusEnum.INITIATED);
    }
  };

  const handleStatusChange = (newStatus, newData = null) => {
    setGcStatus(newStatus);
    if (newData) {
      setGcData(newData);
    }
  };

  if (isGiftCardExpired()) {
    return <GiftCardExpired />;
  }

  if (!referenceId) {
    return (
      <PageWrapper>
        <LoadingContainer>
          <h2>Invalid Request</h2>
          <p>No reference ID provided</p>
        </LoadingContainer>
      </PageWrapper>
    );
  }

  if (loading) {
    return (
      <Container>
        <LoadingContent>
          <SpinnerContainer>
            <Spinner color={red.z_red} size='medium' />
          </SpinnerContainer>
          <LoadingTitle>Loading</LoadingTitle>
          <LoadingSubtitle>Please wait...</LoadingSubtitle>
        </LoadingContent>
      </Container>
    );
  }

  return (
    <PageWrapper>
      <ToastContainer>
        <Toast
          show={!!toastDetails.show}
          align='top'
          onClose={removeToast}
          text={toastDetails.text}
          type={toastDetails.type}
        />
      </ToastContainer>
      {renderContent(
        gcStatus,
        gcData,
        reason,
        handleGenerateCode,
        referenceId,
        handleStatusChange,
      )}
    </PageWrapper>
  );
};

GiftCardPage.propTypes = {
  showErrorToast: PropTypes.func,
  toastDetails: PropTypes.shape({
    show: PropTypes.bool,
    text: PropTypes.string,
    type: PropTypes.string,
  }),
  removeToast: PropTypes.func,
};

GiftCardPage.defaultProps = {
  showErrorToast: _noop,
  toastDetails: { show: false, text: '', type: 'info' },
  removeToast: _noop,
};

export default GiftCardPage;
